import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchAdminMappings,
  createAdminMapping,
  deleteAdminMapping,
  updateAdminMapping,
  fetchAdminAgeGroups,
  fetchAdminCategories,
  fetchAdminProducts,
  fetchAdminSkills,
} from "@/admin/api";
import type {
  AdminMapping,
  AdminProduct,
  AdminCategory,
  AdminAgeGroup,
  AdminSkill,
} from "@/admin/types";
import Pagination from "@/components/feature/Pagination";
import Modal from "@/components/ui/Modal";
import AdminListCard from "@/pages/admin/components/AdminListCard";

type FormState = {
  sallaProductId: string;
  skillId: string;
};

type SortKey = "productName" | "skill" | "category" | "ageGroup" | "stock";
type SortDirection = "asc" | "desc";

export default function AdminMappings() {
  const { t, i18n } = useTranslation("common");
  const isRtl = i18n.dir() === "rtl";

  const [mappings, setMappings] = useState<AdminMapping[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [ageGroups, setAgeGroups] = useState<AdminAgeGroup[]>([]);
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("productName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<AdminMapping | null>(null);

  // Edit Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [mappingToEdit, setMappingToEdit] = useState<AdminMapping | null>(null);
  const [editForm, setEditForm] = useState<FormState>({
    sallaProductId: "",
    skillId: "",
  });
  const [isEditSkillDropdownOpen, setIsEditSkillDropdownOpen] = useState(false);
  const [editSkillSearch, setEditSkillSearch] = useState("");

  // For Add modal multiple skills selection
  const [selectedSkillsList, setSelectedSkillsList] = useState<AdminSkill[]>([]);

  // Form state
  const [form, setForm] = useState<FormState>({
    sallaProductId: "",
    skillId: "",
  });
  const [selectedProduct, setSelectedProduct] = useState<AdminProduct | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState<AdminProduct[]>([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");

  // Action states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const skillDropdownRef = useRef<HTMLDivElement>(null);
  const editSkillDropdownRef = useRef<HTMLDivElement>(null);

  // Locales helpers
  const currentLanguage = useMemo(() => (i18n.language || "ar").split("-")[0], [i18n.language]);
  const defaultLanguage = useMemo(() => {
    const fallback = i18n.options.fallbackLng;
    if (typeof fallback === "string") return fallback;
    if (Array.isArray(fallback)) return fallback[0] || "ar";
    if (fallback && typeof fallback === "object") {
      const entry = Object.values(fallback)[0];
      if (Array.isArray(entry)) return entry[0] || "ar";
      if (typeof entry === "string") return entry;
    }
    return "ar";
  }, [i18n.options.fallbackLng]);

  // Load Initial Data
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [mappingsData, categoriesData, ageGroupsData, skillsData] = await Promise.all([
        fetchAdminMappings(),
        fetchAdminCategories(),
        fetchAdminAgeGroups(),
        fetchAdminSkills(),
      ]);
      setMappings(mappingsData);
      setCategories(categoriesData.filter((c) => c.isActive));
      setAgeGroups(ageGroupsData.filter((g) => g.isActive));
      setSkills(skillsData.filter((s) => s.isActive));
    } catch {
      setError(t("dataLoadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle click outside product & skill dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
      if (editSkillDropdownRef.current && !editSkillDropdownRef.current.contains(event.target as Node)) {
        setIsEditSkillDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced product search
  useEffect(() => {
    if (productSearch.trim() === "") {
      setProductResults([]);
      return;
    }
    setIsSearchingProducts(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAdminProducts(1, 20, productSearch)
        .then((res) => {
          setProductResults(res.data || []);
        })
        .catch(() => {
          setProductResults([]);
        })
        .finally(() => {
          setIsSearchingProducts(false);
        });
    }, 350);

    return () => clearTimeout(delayDebounceFn);
  }, [productSearch]);

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
    if (productSearch.trim() === "" && productResults.length === 0) {
      setIsSearchingProducts(true);
      fetchAdminProducts(1, 10, "")
        .then((res) => {
          setProductResults(res.data || []);
        })
        .catch(() => {})
        .finally(() => {
          setIsSearchingProducts(false);
        });
    }
  };

  // Translations extraction helpers
  const getMappedProductName = (mapping: AdminMapping) => {
    const direct = mapping.productTranslations?.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = mapping.productTranslations?.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return mapping.productNameKey || mapping.productId;
  };

  const getMappedCategoryName = (mapping: AdminMapping) => {
    const direct = mapping.categoryTranslations?.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = mapping.categoryTranslations?.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return mapping.categoryKey;
  };

  const getMappedSkillName = (mapping: AdminMapping) => {
    const direct = mapping.skillTranslations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = mapping.skillTranslations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return mapping.skillKey;
  };

  const getMappedAgeGroupTitle = (mapping: AdminMapping) => {
    const direct = mapping.ageGroupTranslations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = mapping.ageGroupTranslations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return mapping.ageGroupKey;
  };

  const getSkillName = (skill: AdminSkill) => {
    const direct = skill.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = skill.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return skill.key;
  };

  const getSkillCategoryName = (skill: AdminSkill) => {
    const cat = categories.find((c) => c.id === skill.category.id);
    if (!cat) return skill.category.key;
    const direct = cat.translations?.find((item) => item.locale === currentLanguage)?.name;
    if (direct) return direct;
    const fallback = cat.translations?.find((item) => item.locale === defaultLanguage)?.name;
    if (fallback) return fallback;
    return cat.key;
  };

  const getSkillAgeGroupLabel = (skill: AdminSkill) => {
    const group = ageGroups.find((g) => g.id === skill.ageGroup.id);
    if (!group) return skill.ageGroup.key;
    const direct = group.translations?.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = group.translations?.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return group.key;
  };

  const selectedSkill = useMemo(() => {
    return skills.find((s) => s.id === form.skillId) || null;
  }, [skills, form.skillId]);

  const filteredSkills = useMemo(() => {
    const term = skillSearch.trim().toLowerCase();
    if (!term) return skills;
    const words = term.split(/\s+/);
    return skills.filter((skill) => {
      const skillName = getSkillName(skill).toLowerCase();
      const categoryName = getSkillCategoryName(skill).toLowerCase();
      const ageGroupLabel = getSkillAgeGroupLabel(skill).toLowerCase();
      const optionText = `${categoryName} ${ageGroupLabel} ${skillName}`.toLowerCase();
      return words.every((word) => optionText.includes(word));
    });
  }, [skills, skillSearch, currentLanguage, defaultLanguage, categories, ageGroups]);

  const selectedEditSkill = useMemo(() => {
    return skills.find((s) => s.id === editForm.skillId) || null;
  }, [skills, editForm.skillId]);

  const filteredEditSkills = useMemo(() => {
    const term = editSkillSearch.trim().toLowerCase();
    if (!term) return skills;
    const words = term.split(/\s+/);
    return skills.filter((skill) => {
      const skillName = getSkillName(skill).toLowerCase();
      const categoryName = getSkillCategoryName(skill).toLowerCase();
      const ageGroupLabel = getSkillAgeGroupLabel(skill).toLowerCase();
      const optionText = `${categoryName} ${ageGroupLabel} ${skillName}`.toLowerCase();
      return words.every((word) => optionText.includes(word));
    });
  }, [skills, editSkillSearch, currentLanguage, defaultLanguage, categories, ageGroups]);

  const editLinkedMappings = useMemo(() => {
    if (!mappingToEdit) return [];
    return mappings.filter((m) => m.productId === mappingToEdit.productId);
  }, [mappings, mappingToEdit]);

  const handleOpenEditModal = (mapping: AdminMapping) => {
    setMappingToEdit(mapping);
    setEditForm({
      sallaProductId: mapping.productId,
      skillId: "",
    });
    setEditSkillSearch("");
    setIsEditSkillDropdownOpen(false);
    setError("");
    setIsEditModalOpen(true);
  };

  const handleEditAddSkill = async () => {
    if (!mappingToEdit || !editForm.skillId) return;

    setIsSaving(true);
    setError("");
    try {
      const payload = {
        sallaProductId: mappingToEdit.productId,
        skillId: editForm.skillId,
      };

      const newMapping = await createAdminMapping(payload);
      
      setMappings((prev) => [...prev, newMapping]);
      
      setEditForm((prev) => ({ ...prev, skillId: "" }));
      setEditSkillSearch("");
    } catch (err: any) {
      setError(isRtl ? "هذا الربط موجود بالفعل!" : "This mapping already exists!");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditRemoveSkill = async (mappingId: string) => {
    if (!window.confirm(isRtl ? "هل أنت متأكد من إلغاء الربط؟" : "Are you sure you want to unlink?")) return;

    setIsSaving(true);
    setError("");
    try {
      await deleteAdminMapping(mappingId);
      
      setMappings((prev) => prev.filter((m) => m.id !== mappingId));
    } catch (err) {
      setError(isRtl ? "تعذر إلغاء الربط" : "Failed to unlink skill");
    } finally {
      setIsSaving(false);
    }
  };

  // Sort & search handlers
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const getSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return "↕";
    return sortDirection === "asc" ? "▲" : "▼";
  };

  // Processing mappings list
  const filteredAndSortedMappings = useMemo(() => {
    let result = [...mappings];

    // Search query (smart multi-word matching)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const words = term.split(/\s+/);
      result = result.filter((m) => {
        const productName = getMappedProductName(m).toLowerCase();
        const sku = (m.productSku || "").toLowerCase();
        const id = m.productId.toLowerCase();
        const skillName = getMappedSkillName(m).toLowerCase();
        const categoryName = getMappedCategoryName(m).toLowerCase();
        const ageGroup = getMappedAgeGroupTitle(m).toLowerCase();
        
        return words.every(
          (word) =>
            productName.includes(word) ||
            sku.includes(word) ||
            id.includes(word) ||
            skillName.includes(word) ||
            categoryName.includes(word) ||
            ageGroup.includes(word)
        );
      });
    }

    // Sort order
    result.sort((a, b) => {
      let valA: any = "";
      let valB: any = "";

      if (sortKey === "productName") {
        valA = getMappedProductName(a);
        valB = getMappedProductName(b);
      } else if (sortKey === "skill") {
        valA = getMappedSkillName(a);
        valB = getMappedSkillName(b);
      } else if (sortKey === "category") {
        valA = getMappedCategoryName(a);
        valB = getMappedCategoryName(b);
      } else if (sortKey === "ageGroup") {
        valA = getMappedAgeGroupTitle(a);
        valB = getMappedAgeGroupTitle(b);
      } else if (sortKey === "stock") {
        valA = a.productStock;
        valB = b.productStock;
      }

      if (typeof valA === "string") {
        return valA.localeCompare(valB, undefined, { sensitivity: "base" }) * (sortDirection === "asc" ? 1 : -1);
      }
      return (valA - valB) * (sortDirection === "asc" ? 1 : -1);
    });

    return result;
  }, [mappings, searchTerm, sortKey, sortDirection, currentLanguage, defaultLanguage]);

  const totalResults = filteredAndSortedMappings.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedMappings = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredAndSortedMappings.slice(start, start + pageSize);
  }, [filteredAndSortedMappings, safePage, pageSize]);

  // Sync safe page
  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  // Modal actions
  const resetAddForm = () => {
    setForm({ sallaProductId: "", skillId: "" });
    setSelectedProduct(null);
    setProductSearch("");
    setProductResults([]);
    setSkillSearch("");
    setSelectedSkillsList([]);
    setError("");
    setSuccess("");
  };

  const handleOpenAddModal = () => {
    resetAddForm();
    setIsAddModalOpen(true);
  };

  const handleCreateMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.sallaProductId || selectedSkillsList.length === 0) {
      setError(t("adminFillRequired"));
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const payload = {
        sallaProductId: form.sallaProductId,
        skillIds: selectedSkillsList.map((s) => s.id),
      };

      const newMappings = await createAdminMapping(payload);
      if (Array.isArray(newMappings)) {
        setMappings((prev) => {
          const existingKeys = new Set(prev.map((m) => `${m.productId}_${m.skillId}`));
          const added = newMappings.filter(
            (nm: any) => !existingKeys.has(`${nm.productId}_${nm.skillId}`)
          );
          return [...prev, ...added];
        });
      } else {
        setMappings((prev) => [...prev, newMappings]);
      }
      setIsAddModalOpen(false);
      resetAddForm();
    } catch (err: any) {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDeleteConfirm = (mapping: AdminMapping) => {
    setMappingToDelete(mapping);
    setIsDeleteConfirmOpen(true);
    setError("");
  };

  const handleDeleteMapping = async () => {
    if (!mappingToDelete) return;
    setIsDeleting(true);
    setError("");
    try {
      await deleteAdminMapping(mappingToDelete.id);
      setMappings((prev) => prev.filter((m) => m.id !== mappingToDelete.id));
      setIsDeleteConfirmOpen(false);
      setMappingToDelete(null);
    } catch {
      setError(isRtl ? "فشل حذف الربط!" : "Unable to delete mapping!");
    } finally {
      setIsDeleting(false);
    }
  };

  // Category Color hex formatting
  const getCategoryBadge = (mapping: AdminMapping) => {
    const color = mapping.categoryColorHex || "#E8927C";
    const name = getMappedCategoryName(mapping);

    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
        style={{ backgroundColor: `${color}15`, color: color }}
      >
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        {name}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminMappings")}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {isRtl
              ? "إدارة وربط منتجات المتجر بالأركان التطورية والفئات العمرية المناسبة لتوصيتها للأهالي."
              : "Manage and link store products with growth pillars and targeted age groups to recommend to parents."}
          </p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {isRtl ? "إضافة ربط منتج" : "Add Product Mapping"}
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="w-full md:max-w-md relative">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isRtl ? "البحث بالاسم، SKU أو معرف المتجر..." : "Search by product name, SKU or store ID..."}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 bg-gray-50/10"
          />
          <div className={`absolute top-1/2 -translate-y-1/2 text-gray-400 ${isRtl ? "right-3.5" : "left-3.5"}`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="text-xs text-gray-500 font-semibold self-end md:self-center">
          {t("adminResultsCount", { count: totalResults })}
        </div>
      </div>

      {/* Error Banner */}
      {error && !isAddModalOpen && !isDeleteConfirmOpen && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-20 text-center text-sm text-gray-500">{t("loading")}</div>
        ) : paginatedMappings.length === 0 ? (
          <div className="py-20 text-center text-sm text-gray-400 flex flex-col items-center justify-center gap-2">
            <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            <span>{t("adminNoResults")}</span>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="bg-gray-50/50 text-xs font-semibold text-gray-500 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleSort("productName")}
                        className="flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                      >
                        {isRtl ? "المنتج والتفاصيل" : "Product Details"}
                        <span className="text-[10px] opacity-70">{getSortIndicator("productName")}</span>
                      </button>
                    </th>
                    <th className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleSort("skill")}
                        className="flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                      >
                        {isRtl ? "المهارة المستهدفة" : "Target Skill"}
                        <span className="text-[10px] opacity-70">{getSortIndicator("skill")}</span>
                      </button>
                    </th>
                    <th className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleSort("category")}
                        className="flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                      >
                        {t("adminMappingCategory")}
                        <span className="text-[10px] opacity-70">{getSortIndicator("category")}</span>
                      </button>
                    </th>
                    <th className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleSort("ageGroup")}
                        className="flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                      >
                        {t("adminMappingAgeGroup")}
                        <span className="text-[10px] opacity-70">{getSortIndicator("ageGroup")}</span>
                      </button>
                    </th>
                    <th className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => handleSort("stock")}
                        className="flex items-center gap-1 hover:text-gray-800 focus:outline-none"
                      >
                        {t("adminProductStock")}
                        <span className="text-[10px] opacity-70">{getSortIndicator("stock")}</span>
                      </button>
                    </th>
                    <th className="px-6 py-4 text-center">{t("adminActions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedMappings.map((mapping) => {
                    const productName = getMappedProductName(mapping);
                    const isLowStock = mapping.productStock <= 5;
                    return (
                      <tr key={mapping.id} className="hover:bg-gray-50/40 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={mapping.productImageUrl}
                              alt=""
                              className="w-12 h-12 rounded-xl object-cover border border-gray-100 bg-gray-50 shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 truncate max-w-xs">{productName}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                {mapping.productSku && (
                                  <span className="text-[10px] text-gray-500 font-mono">SKU: {mapping.productSku}</span>
                                )}
                                <span className="text-[10px] text-gray-400 font-mono">ID: {mapping.productId}</span>
                                <span className="text-[10px] font-medium text-gray-700">
                                  {mapping.productPrice} {t("sar")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-800 font-semibold text-xs">
                          {getMappedSkillName(mapping)}
                        </td>
                        <td className="px-6 py-4">{getCategoryBadge(mapping)}</td>
                        <td className="px-6 py-4 text-gray-600 font-semibold text-xs">
                          {getMappedAgeGroupTitle(mapping)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                              isLowStock
                                ? "bg-red-50 text-red-700 animate-pulse"
                                : "bg-green-50 text-green-700"
                            }`}
                          >
                            {mapping.productStock}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Edit Button */}
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(mapping)}
                              className="p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-xl transition-colors"
                              title={isRtl ? "تعديل" : "Edit"}
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => handleOpenDeleteConfirm(mapping)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-colors"
                              title={isRtl ? "حذف" : "Delete"}
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden p-4 space-y-4">
              {paginatedMappings.map((mapping) => {
                const productName = getMappedProductName(mapping);
                const isLowStock = mapping.productStock <= 5;
                return (
                  <AdminListCard
                    key={mapping.id}
                    title={productName}
                    iconUrl={mapping.productImageUrl || undefined}
                    iconFallback="ri-link"
                    onEdit={() => handleOpenEditModal(mapping)}
                    badges={[
                      { label: getMappedSkillName(mapping), icon: "ri-lightbulb-line" },
                      { label: getMappedAgeGroupTitle(mapping), icon: "ri-calendar-todo-line" },
                      { label: `${mapping.productPrice} ${t("sar")}`, icon: "ri-money-dollar-circle-line" },
                      {
                        label: `${mapping.productStock}`,
                        icon: isLowStock ? "ri-error-warning-line" : "ri-checkbox-circle-line",
                      },
                    ]}
                    footerLeft={getCategoryBadge(mapping)}
                    customActions={
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(mapping)}
                          className="p-2 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-xl border border-gray-100 transition-colors"
                        >
                          <i className="ri-edit-line text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenDeleteConfirm(mapping)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl border border-gray-100 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-lg" />
                        </button>
                      </div>
                    }
                  />
                );
              })}
            </div>

            {/* Pagination controls footer */}
            <div className="border-t border-gray-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          </>
        )}
      </div>

      {/* Add New Mapping Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={isRtl ? "ربط منتج جديد بالفئات والأركان" : "Add New Product Mapping"}
        maxWidth="2xl"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleCreateMapping}
              disabled={isSaving || !form.sallaProductId || !form.skillId}
              className="px-5 py-2 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-sm transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {t("adminCreate")}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreateMapping} className="space-y-5">
          {error && (
            <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Product selector search input */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-700">
              {isRtl ? "المنتج المستهدف" : "Target Product"} <span className="text-red-500">*</span>
            </label>

            {selectedProduct ? (
              <div className="flex items-center justify-between p-3.5 border border-teal-100 bg-teal-50/10 rounded-xl">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedProduct.imageUrl}
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover border border-gray-100 bg-gray-50 shrink-0"
                  />
                  <div className="min-w-0">
                    <h4 className="text-xs font-semibold text-gray-800 truncate">
                      {selectedProduct.translations.find((t) => t.locale === currentLanguage)?.name ||
                        selectedProduct.translations.find((t) => t.locale === defaultLanguage)?.name ||
                        selectedProduct.nameKey}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {selectedProduct.sku && (
                        <span className="text-[10px] text-gray-500 font-mono">SKU: {selectedProduct.sku}</span>
                      )}
                      <span className="text-[10px] text-gray-400 font-mono">ID: {selectedProduct.id}</span>
                      <span className="text-[10px] font-semibold text-gray-700">
                        {selectedProduct.price} {t("sar")}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProduct(null);
                    setForm((prev) => ({ ...prev, sallaProductId: "" }));
                    setProductSearch("");
                  }}
                  className="text-xs font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  {t("cancel")}
                </button>
              </div>
            ) : (
              <>
                <div className="relative" ref={dropdownRef}>
                  <input
                    type="text"
                    placeholder={isRtl ? "ابحث عن منتج بالاسم أو الـ SKU أو المعرف..." : "Search for a product by name, SKU, or ID..."}
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={handleInputFocus}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-gray-50/10"
                  />
                  {isDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                      {isSearchingProducts ? (
                        <div className="p-4 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          <span>{t("loading")}</span>
                        </div>
                      ) : productResults.length === 0 ? (
                        <div className="p-4 text-center text-xs text-gray-400">{t("adminNoResults")}</div>
                      ) : (
                        productResults.map((product) => {
                          const name = product.translations.find((t) => t.locale === currentLanguage)?.name ||
                            product.translations.find((t) => t.locale === defaultLanguage)?.name ||
                            product.nameKey;
                          return (
                            <button
                              key={product.id}
                              type="button"
                              onClick={() => {
                                setSelectedProduct(product);
                                setForm((prev) => ({ ...prev, sallaProductId: product.id }));
                                setIsDropdownOpen(false);
                              }}
                              className="w-full text-right p-3 hover:bg-teal-50/5 flex items-center gap-3 transition-colors text-right"
                            >
                              <img
                                src={product.imageUrl}
                                alt=""
                                className="w-9 h-9 rounded-md object-cover border border-gray-100 bg-gray-50 shrink-0"
                              />
                              <div className="flex-1 min-w-0 text-right">
                                <p className="text-xs font-semibold text-gray-700 truncate">{name}</p>
                                <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                                  SKU: {product.sku || "N/A"} • ID: {product.id}
                                </p>
                              </div>
                              <div className="text-left">
                                <p className="text-xs font-bold text-gray-800">{product.price} {t("sar")}</p>
                                <p className="text-[10px] text-gray-400">{t("adminProductStock")}: {product.stockQuantity}</p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {isDropdownOpen && <div className="h-60" />}
              </>
            )}
          </div>

          {/* Skill selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-xs font-bold text-gray-700">
                {isRtl ? "ربط بالمهارات" : "Link to Skills"} <span className="text-red-500">*</span>
              </label>
              
              <div className="relative" ref={skillDropdownRef}>
                <input
                  type="text"
                  placeholder={isRtl ? "ابحث عن مهارة بالاسم، الركن، أو العمر لربطها..." : "Search for a skill by name, category, or age to link..."}
                  value={skillSearch}
                  onChange={(e) => {
                    setSkillSearch(e.target.value);
                    setIsSkillDropdownOpen(true);
                  }}
                  onFocus={() => setIsSkillDropdownOpen(true)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-gray-50/10 font-medium"
                />
                {isSkillDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                    {filteredSkills.length === 0 ? (
                      <div className="p-4 text-center text-xs text-gray-400">{t("adminNoResults")}</div>
                    ) : (
                      filteredSkills.map((skill) => {
                        const sName = getSkillName(skill);
                        const cName = getSkillCategoryName(skill);
                        const aLabel = getSkillAgeGroupLabel(skill);
                        const isAlreadySelected = selectedSkillsList.some((s) => s.id === skill.id);

                        return (
                          <button
                            key={skill.id}
                            type="button"
                            disabled={isAlreadySelected}
                            onClick={() => {
                              setSelectedSkillsList((prev) => [...prev, skill]);
                              setIsSkillDropdownOpen(false);
                              setSkillSearch("");
                            }}
                            className={`w-full text-right p-3 hover:bg-teal-50/5 flex items-center justify-between gap-3 transition-colors text-right ${
                              isAlreadySelected ? "opacity-40 cursor-not-allowed" : ""
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                                {skill.iconUrl ? (
                                  <img
                                    src={skill.iconUrl}
                                    alt=""
                                    className="w-5 h-5 object-contain"
                                  />
                                ) : (
                                  <span className="text-xs text-gray-400">💡</span>
                                )}
                              </div>
                              <div className="min-w-0 text-right">
                                <p className="text-xs font-semibold text-gray-700 truncate">{sName}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  {cName} | {aLabel}
                                </p>
                              </div>
                            </div>
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: skill.category.colorHex }}
                              title={cName}
                            />
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              {isSkillDropdownOpen && <div className="h-60" />}
            </div>

            {/* List of Selected Skills */}
            {selectedSkillsList.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-bold text-gray-700">
                  {isRtl ? "المهارات المختارة للربط" : "Selected Skills to Link"}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedSkillsList.map((skill) => {
                    const sName = getSkillName(skill);
                    const cName = getSkillCategoryName(skill);
                    const aLabel = getSkillAgeGroupLabel(skill);

                    return (
                      <div key={skill.id} className="flex items-center justify-between p-2.5 border border-teal-100 bg-teal-50/5 rounded-xl">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-xs">💡</span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-700 truncate">{sName}</p>
                            <p className="text-[9px] text-gray-400 mt-0.5">
                              {cName} | {aLabel}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedSkillsList((prev) => prev.filter((s) => s.id !== skill.id))}
                          className="text-xs font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title={isRtl ? "تأكيد حذف ربط المنتج" : "Confirm Delete Mapping"}
        maxWidth="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleDeleteMapping}
              disabled={isDeleting}
              className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-sm transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isDeleting && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isRtl ? "حذف الربط" : "Delete Mapping"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {isRtl
              ? "هل أنت متأكد من رغبتك في حذف ربط هذا المنتج؟ لن يؤثر هذا الإجراء على المنتج نفسه في كاش المتجر، ولكنه سيزيله من التوصيات الخاصة بهذا الركن والفئة العمرية."
              : "Are you sure you want to delete this product mapping? This action will not delete the product from the store cache, but it will remove it from the recommendations for this category and age group."}
          </p>
          {mappingToDelete && (
            <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl flex items-center gap-3">
              <img
                src={mappingToDelete.productImageUrl}
                alt=""
                className="w-12 h-12 rounded-lg object-cover border border-gray-200 bg-white shrink-0"
              />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700 truncate">{getMappedProductName(mappingToDelete)}</p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                  <span>{getMappedSkillName(mappingToDelete)}</span>
                  <span>•</span>
                  <span>{getMappedCategoryName(mappingToDelete)}</span>
                  <span>•</span>
                  <span>{getMappedAgeGroupTitle(mappingToDelete)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Mapping Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={isRtl ? "إدارة مهارات المنتج المرتبطة" : "Manage Linked Product Skills"}
        maxWidth="2xl"
        footer={
          <button
            type="button"
            onClick={() => setIsEditModalOpen(false)}
            className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors"
          >
            {isRtl ? "إغلاق" : "Close"}
          </button>
        }
      >
        <div className="space-y-5">
          {error && (
            <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Read-Only Product Info */}
          {mappingToEdit && (
            <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
              <img
                src={mappingToEdit.productImageUrl}
                alt=""
                className="w-12 h-12 rounded-xl object-cover border border-gray-100 bg-white shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-xs font-bold text-gray-800 truncate">
                  {getMappedProductName(mappingToEdit)}
                </h4>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {mappingToEdit.productSku && (
                    <span className="text-[10px] text-gray-500 font-mono">SKU: {mappingToEdit.productSku}</span>
                  )}
                  <span className="text-[10px] text-gray-400 font-mono">ID: {mappingToEdit.productId}</span>
                  <span className="text-[10px] font-semibold text-gray-700">
                    {mappingToEdit.productPrice} {t("sar")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Add New Skill Section */}
          <div className="space-y-2 p-4 bg-teal-50/10 border border-teal-100/50 rounded-xl">
            <label className="block text-xs font-bold text-gray-700">
              {isRtl ? "ربط مهارة جديدة" : "Link New Skill"}
            </label>

            {selectedEditSkill ? (
              <div className="flex items-center justify-between p-3 border border-teal-100 bg-teal-50/20 rounded-xl">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                    {selectedEditSkill.iconUrl ? (
                      <img
                        src={selectedEditSkill.iconUrl}
                        alt=""
                        className="w-6 h-6 object-contain"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">💡</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-semibold text-gray-800 truncate">
                      {getSkillName(selectedEditSkill)}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{
                          backgroundColor: `${selectedEditSkill.category.colorHex}15`,
                          color: selectedEditSkill.category.colorHex,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: selectedEditSkill.category.colorHex }}
                        />
                        {getSkillCategoryName(selectedEditSkill)}
                      </span>
                      <span className="text-[10px] font-semibold text-gray-500">
                        {getSkillAgeGroupLabel(selectedEditSkill)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleEditAddSkill}
                    disabled={isSaving}
                    className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                  >
                    {isSaving && (
                      <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {isRtl ? "ربط الآن" : "Link Now"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditForm((prev) => ({ ...prev, skillId: "" }));
                      setEditSkillSearch("");
                    }}
                    className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2.5 py-2 rounded-lg transition-colors"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="relative" ref={editSkillDropdownRef}>
                  <input
                    type="text"
                    placeholder={isRtl ? "ابحث عن مهارة بالاسم، الركن، أو العمر لربطها..." : "Search for a skill by name, category, or age to link..."}
                    value={editSkillSearch}
                    onChange={(e) => {
                      setEditSkillSearch(e.target.value);
                      setIsEditSkillDropdownOpen(true);
                    }}
                    onFocus={() => setIsEditSkillDropdownOpen(true)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 text-sm bg-white font-medium shadow-sm"
                  />
                  {isEditSkillDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                      {filteredEditSkills.length === 0 ? (
                        <div className="p-4 text-center text-xs text-gray-400">{t("adminNoResults")}</div>
                      ) : (
                        filteredEditSkills.map((skill) => {
                          const sName = getSkillName(skill);
                          const cName = getSkillCategoryName(skill);
                          const aLabel = getSkillAgeGroupLabel(skill);
                          return (
                            <button
                              key={skill.id}
                              type="button"
                              onClick={() => {
                                setEditForm((prev) => ({ ...prev, skillId: skill.id }));
                                setIsEditSkillDropdownOpen(false);
                              }}
                              className="w-full text-right p-3 hover:bg-teal-50/5 flex items-center justify-between gap-3 transition-colors text-right"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0">
                                  {skill.iconUrl ? (
                                    <img
                                      src={skill.iconUrl}
                                      alt=""
                                      className="w-5 h-5 object-contain"
                                    />
                                  ) : (
                                    <span className="text-xs text-gray-400">💡</span>
                                  )}
                                </div>
                                <div className="min-w-0 text-right">
                                  <p className="text-xs font-semibold text-gray-700 truncate">{sName}</p>
                                  <p className="text-[10px] text-gray-400 mt-0.5">
                                    {cName} | {aLabel}
                                  </p>
                                </div>
                              </div>
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: skill.category.colorHex }}
                                title={cName}
                              />
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {isEditSkillDropdownOpen && <div className="h-60" />}
              </>
            )}
          </div>

          {/* Currently Linked Skills List */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-gray-700 px-1">
              {isRtl ? `المهارات المرتبطة حالياً (${editLinkedMappings.length})` : `Currently Linked Skills (${editLinkedMappings.length})`}
            </h4>

            {editLinkedMappings.length === 0 ? (
              <div className="text-center p-8 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-xs text-gray-400">
                {isRtl ? "لا توجد مهارات مرتبطة بهذا المنتج بعد." : "No skills linked to this product yet."}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {editLinkedMappings.map((mapping) => {
                  const skillName = getMappedSkillName(mapping);
                  const categoryName = getMappedCategoryName(mapping);
                  const ageGroupTitle = getMappedAgeGroupTitle(mapping);
                  const color = mapping.categoryColorHex || "#E8927C";

                  return (
                    <div key={mapping.id} className="flex items-center justify-between p-3 border border-gray-100 bg-white rounded-xl shadow-sm hover:border-gray-200 transition-all">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 text-xs">
                          💡
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-700 truncate">{skillName}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                              style={{ backgroundColor: `${color}15`, color: color }}
                            >
                              {categoryName}
                            </span>
                            <span className="text-[9px] text-gray-400 font-semibold">{ageGroupTitle}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleEditRemoveSkill(mapping.id)}
                        disabled={isSaving}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors shrink-0 disabled:opacity-50"
                        title={isRtl ? "إلغاء الربط" : "Remove Link"}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
